import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { ErrorNote, InfoNote } from '@/components/Form';
import { presentApiError } from '@/lib/form-errors';
import { useCreateReport } from './use-safety';

/**
 * Reporting a person from wherever they appear. [Parent step 17]
 *
 * Kept quiet until pressed — a report link shouting at the top of every card
 * would make an ordinary marketplace feel adversarial. Once opened it asks for
 * one thing and nothing else, because someone reporting a problem should not
 * have to fill in a form to be heard.
 */
export function ReportButton({
  personId,
  personName,
  jobId,
}: {
  /** Any publicId for the person: teen, parent, poster or account. */
  personId: string;
  personName: string;
  jobId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const report = useCreateReport();

  async function submit(): Promise<void> {
    setError(null);
    try {
      await report.mutateAsync({
        reportedPersonId: personId,
        ...(jobId ? { jobId } : {}),
        reason: reason.trim(),
      });
      setSent(true);
      setReason('');
    } catch (caught) {
      setError(presentApiError(caught, 'Could not send that report').message);
    }
  }

  if (sent) {
    return (
      <InfoNote>
        Reported. Our team will look at it — you will not hear from {personName} about this.
      </InfoNote>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs font-semibold text-slate-400 underline-offset-2 transition hover:text-red-600 hover:underline"
      >
        Report {personName}
      </button>
    );
  }

  return (
    <div className="w-full rounded-xl border border-line bg-canvas p-4">
      <p className="text-sm font-bold text-ink">Report {personName}</p>
      <p className="mt-0.5 text-xs text-slate-500">
        They are not told who reported them. If someone is in danger, call your local emergency
        number first.
      </p>

      <div className="mt-3">
        <Textarea
          label="What happened?"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={3}
          maxLength={4000}
          placeholder="A sentence is enough."
        />
      </div>

      {error && (
        <div className="mt-2">
          <ErrorNote message={error} />
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <Button
          variant="danger"
          disabled={report.isPending || reason.trim().length < 10}
          onClick={() => void submit()}
        >
          {report.isPending ? 'Sending…' : 'Send report'}
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
